<script lang="ts">
  import { onDestroy } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type {
    DemoDataResetJobStartResponse,
    DemoDataResetJobView,
    DemoDataResetResponse,
  } from "../../../../../../../../../../../../../../api/kehrblatt/demo-data"
  import { getDemoOrganizationResetJob } from "../../../../../../../../../../../../../../api/kehrblatt/demo-data"
  import { ApiError } from "../../../../../../../../../../../../../../api/kehrblatt/client"
  import WorkflowActionBox from "../../_shared/_modules/workflow-action-box"
  import WorkflowStepFrame from "../../_shared/_modules/workflow-step-frame"
  import BackgroundJobProgressCard from "../../../../../../../../../../../../_shared/_modules/background-job-progress-card"
  import type {
    DemoResetMemberOutput,
    DemoResetRunPhase,
  } from "../../_shared/_logic/demo-data-reset-contract"
  import {
    abortableDelay,
    clampPollDelay,
    isAbortError,
    pollFailuresExhausted,
    retryAfterPollDelay,
    STATUS_POLL_REQUEST_TIMEOUT_MS,
    transientPollDelay,
  } from "./_logic/polling"
  import { projectDemoResetJobProgress } from "./_logic/presentation"
  import DownloadStreetListsAction from "./_modules/download-street-lists-action"
  import StartDemoResetAction from "./_modules/start-demo-reset-action"

  const {
    ctx,
    organizationId,
    primary,
    secondary,
    onJobStarted,
    onPhaseChange,
    onOutput,
  }: {
    ctx: ProgressStepContext
    organizationId: string
    primary: DemoResetMemberOutput
    secondary: DemoResetMemberOutput
    onJobStarted: () => void
    onPhaseChange: (phase: DemoResetRunPhase) => void
    onOutput: (result: DemoDataResetResponse) => void
  } = $props()

  let jobId = $state<string | null>(null)
  let job = $state<DemoDataResetJobView | null>(null)
  type StatusIssue =
    | { kind: "retrying"; message: string }
    | { kind: "unavailable"; message: string; recoveryMessage: string }

  let statusIssue = $state<StatusIssue | null>(null)
  let pollController: AbortController | null = null
  const progressView = $derived(job ? projectDemoResetJobProgress(job) : null)

  const markStatusUnavailable = (
    message: string,
    recoveryMessage = "Sie können den Status erneut laden oder dieses Fenster schließen. Der Reset läuft im Hintergrund weiter.",
  ) => {
    statusIssue = { kind: "unavailable", message, recoveryMessage }
    onPhaseChange("status_unavailable")
  }

  const handleTerminal = (current: DemoDataResetJobView): boolean => {
    if (current.status === "succeeded") {
      pollController?.abort()

      if (!current.result) {
        markStatusUnavailable(
          "Der Reset wurde abgeschlossen, aber das Ergebnis kann gerade nicht geladen werden.",
          "Laden Sie den Status erneut, um anschließend die ISI-Exporte herunterzuladen.",
        )

        return true
      }

      statusIssue = null
      onPhaseChange("succeeded_waiting_download")

      return true
    }

    if (current.status === "failed") {
      pollController?.abort()
      statusIssue = null
      onPhaseChange("failed")

      return true
    }

    return false
  }

  const loadCurrentStatus = async (currentJobId: string, parentSignal: AbortSignal) => {
    const requestController = new AbortController()
    const abortRequest = () => requestController.abort(parentSignal.reason)
    parentSignal.addEventListener("abort", abortRequest, { once: true })
    const timeout = window.setTimeout(
      () => requestController.abort(new DOMException("Status request timed out", "TimeoutError")),
      STATUS_POLL_REQUEST_TIMEOUT_MS,
    )
    try {
      return await getDemoOrganizationResetJob(organizationId, currentJobId, {
        signal: requestController.signal,
      })
    } catch (error) {
      if (requestController.signal.reason instanceof DOMException) {
        throw requestController.signal.reason
      }

      throw error
    } finally {
      window.clearTimeout(timeout)
      parentSignal.removeEventListener("abort", abortRequest)
    }
  }

  const monitor = async (currentJobId: string, immediate = false) => {
    if (job && handleTerminal(job)) return

    const controller = new AbortController()
    pollController?.abort()
    pollController = controller
    let delay = clampPollDelay(job?.poll_after_ms)
    let consecutiveFailures = 0
    let waitBeforeRequest = !immediate
    try {
      while (!controller.signal.aborted) {
        try {
          if (waitBeforeRequest) await abortableDelay(delay, controller.signal)

          waitBeforeRequest = true
          const current = await loadCurrentStatus(currentJobId, controller.signal)
          job = current
          consecutiveFailures = 0
          statusIssue = null

          if (handleTerminal(current)) return

          delay = clampPollDelay(current.poll_after_ms)
        } catch (error) {
          if (isAbortError(error) || controller.signal.aborted) return

          const rateLimited = error instanceof ApiError && error.status === 429

          if (
            error instanceof ApiError &&
            error.status >= 400 &&
            error.status < 500 &&
            !rateLimited
          ) {
            markStatusUnavailable("Der Status kann nicht mehr abgerufen werden.")

            return
          }

          consecutiveFailures += 1

          if (pollFailuresExhausted(consecutiveFailures)) {
            markStatusUnavailable("Der Status kann gerade nicht geladen werden.")

            return
          }

          statusIssue = {
            kind: "retrying",
            message:
              "Die Verbindung wurde unterbrochen. Der Status wird automatisch erneut geladen.",
          }
          delay = rateLimited
            ? (retryAfterPollDelay(error) ??
              transientPollDelay(job?.poll_after_ms, consecutiveFailures))
            : transientPollDelay(job?.poll_after_ms, consecutiveFailures)
        }
      }
    } finally {
      if (pollController === controller) pollController = null
    }
  }

  // The start response only names the admitted job; its status is read right away.
  const handleStarted = (started: DemoDataResetJobStartResponse) => {
    jobId = started.job_id
    statusIssue = null
    onJobStarted()
    onPhaseChange("running")
    void monitor(started.job_id, true)
  }

  const retryStatus = () => {
    if (!jobId || job?.status === "failed" || (job?.status === "succeeded" && job.result)) return

    statusIssue = null
    onPhaseChange("running")
    void monitor(jobId, true)
  }

  const handleSubmittingChange = (submitting: boolean) => {
    if (submitting) {
      onPhaseChange("enqueueing")

      return
    }

    if (!jobId) onPhaseChange("ready")
  }

  const handleDownloaded = () => {
    if (!job?.result) return

    onOutput(job.result)
    onPhaseChange("downloaded")
  }

  onDestroy(() => pollController?.abort())
</script>

<WorkflowStepFrame>
  <SectionTitle
    title="Testzugang zurücksetzen"
    description="Prüfen Sie die Angaben, starten Sie den Reset und laden Sie anschließend beide ISI-Exporte herunter."
    helpModalTextId="organizationTestDataReset"
    helpModalTooltip="Hilfe zu diesem Schritt öffnen"
    small={true}
    darkMode={true}
    styleOuter="margin: 0;"
  />
  <WorkflowActionBox label="Zurücksetzen und ISI-Exporte erstellen">
    {#if !jobId}
      <p>
        Der bestehende Testdatenbestand wird vollständig ersetzt. {primary.displayName} (Chef) und
        {secondary.displayName} (Geselle) erhalten jeweils die Hälfte der Liegenschaften; alle
        Ansprechpartner einer Liegenschaft bleiben dabei demselben Profil zugeordnet. Zugänge,
        Rollen und Passwörter beider Mitglieder bleiben unverändert.
      </p>
      <p
        >Nach dem Reset laden Sie je eine ISI-Export mit zehn Liegenschaften für Chef und Geselle
        herunter.</p
      >
    {:else}
      {#if statusIssue?.kind === "unavailable"}
        <div class="status-unavailable" role="alert">
          <strong>Status kann nicht geladen werden</strong>
          <span>{statusIssue.message}</span>
          <span>{statusIssue.recoveryMessage}</span>
        </div>
      {:else if progressView}
        <BackgroundJobProgressCard view={progressView} />
        {#if job?.phase === "validating" && job.completed_steps === 0 && (job.status === "running" || job.status === "queued")}
          <p role="status">Verbundene Kalender werden vor dem Neuaufbau der Testdaten bereinigt.</p>
        {/if}
      {:else}
        <p role="status">Reset wird vorbereitet…</p>
      {/if}
      {#if statusIssue?.kind === "retrying"}
        <p class="connection-notice" role="status">{statusIssue.message}</p>
      {/if}
      {#if job?.status === "succeeded" && job.result}
        <div class="download-ready" role="status">
          <strong>Beide ISI-Exporte sind bereit.</strong>
          <span
            >Laden Sie die Chef- und Gesellenliste gemeinsam herunter, um den Reset abzuschließen.</span
          >
        </div>
      {/if}
    {/if}
    {#snippet actions()}
      {#if !jobId}
        <StartDemoResetAction
          {organizationId}
          {primary}
          {secondary}
          onSubmittingChange={handleSubmittingChange}
          onStarted={handleStarted}
        />
      {:else if job?.status === "succeeded" && job.result}
        <DownloadStreetListsAction
          {organizationId}
          jobId={job.job_id}
          onDownloaded={handleDownloaded}
        />
      {:else if statusIssue?.kind === "unavailable"}
        <CTA type="secondaryButton" content="Status erneut laden" onClick={retryStatus} />
      {/if}
    {/snippet}
  </WorkflowActionBox>
</WorkflowStepFrame>

<style lang="less">
  .connection-notice {
    border-left: 0.25rem solid var(--warning-100);
    padding: 0.7rem;
    font-weight: 700;
  }

  .status-unavailable {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    border: 1px solid color-mix(in srgb, var(--error-100) 62%, transparent);
    background: color-mix(in srgb, var(--error-100) 10%, transparent);
    padding: 1rem;
    color: var(--text-100);
  }

  .status-unavailable strong {
    color: var(--error-100);
  }

  .download-ready {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-left: 0.25rem solid var(--success-100);
    padding: 0.7rem;
  }
</style>
